import AdminShell, { Body, Section, Stat } from "@/components/admin/AdminShell";
import { Card } from "@/components/Common";
import { DailyBriefing } from "@/components/admin/DailyBriefing";
import { TodayCalls } from "@/components/rcc/li/TodayCalls";
import { label, num, pct, useAdminGet, usd } from "@/lib/admin";

const Funnel = ({ rows = [] }) => {
  const max = Math.max(1, ...rows.map((r) => r.count || 0));
  return (
    <Card className="space-y-2.5" data-testid="cc-funnel">
      {rows.map((r, i) => (
        <div key={r.stage} className="grid grid-cols-[150px_1fr_70px] items-center gap-3 text-xs sm:grid-cols-[200px_1fr_80px]" data-testid={`cc-funnel-${r.stage}`}>
          <span className="truncate text-zinc-300">{label(r.stage)}</span>
          <div className="h-2.5 overflow-hidden rounded-full bg-[#27272A]"><div className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F3C649]" style={{ width: `${(100 * (r.count || 0)) / max}%` }} /></div>
          <span className="text-right font-mono text-zinc-200">{num(r.count)}{i > 0 && r.drop_off > 0 && <span className="ml-1.5 text-red-400" data-testid={`cc-funnel-drop-${r.stage}`}>−{Math.round(r.drop_off)}%</span>}</span>
        </div>
      ))}
    </Card>
  );
};

export default function CCOverview() {
  const q = useAdminGet("/admin/cc/overview");
  return (
    <AdminShell title="Executive Overview" subtitle="Acquisition, revenue and email at a glance.">
      <TodayCalls />
      <DailyBriefing />
      <Body q={q}>{(d) => {
        const a = d.acquisition || {}, r = d.revenue || {}, e = d.email || {};
        return (
          <>
            <Section title="Revenue (actual)" testid="cc-revenue">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat k="MRR" v={usd(r.mrr)} testid="cc-mrr" />
                <Stat k="Actual revenue, all time" v={usd(r.actual_revenue_total)} tone="text-emerald-300" testid="cc-actual-revenue" />
                <Stat k="New revenue this month" v={usd(r.new_revenue_month)} />
                <Stat k="Active subscribers" v={num(r.active_subscribers)} sub={`+${num(r.new_subscribers_month)} this month`} />
                <Stat k="Avg revenue per customer" v={usd(r.arpc)} />
                <Stat k="Churn this month" v={num(r.churn_month)} />
              </div>
              <Card className="mt-3 border-dashed border-[#D4AF37]/40 bg-[#33290D]/20 p-4" data-testid="cc-estimated-opportunity">
                <div className="text-[11px] uppercase tracking-wider text-[#D4AF37]">Estimated opportunity · not real revenue</div>
                <div className="mt-1 font-display text-2xl text-[#F3C649]">{usd(r.estimated_opportunity_mrr)}<span className="text-sm text-zinc-500">/mo</span></div>
                <p className="mt-1 text-xs text-zinc-500">Intent score × Pro price across open prospects. A forecast, not money received.</p>
              </Card>
            </Section>
            <Section title="Acquisition" testid="cc-acquisition">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat k="Total prospects" v={num(a.total_prospects)} sub={`${num(a.new_today)} today · ${num(a.new_week)} this week`} />
                <Stat k="Resume uploads" v={num(a.resume_uploads)} />
                <Stat k="Assessments" v={num(a.assessments)} sub={`${pct(a.assessment_completion_rate)} completion`} />
                <Stat k="Free → paid" v={pct(a.free_to_paid)} sub={`Assessment → paid ${pct(a.assessment_to_paid)}`} />
              </div>
            </Section>
            <Section title="Email" testid="cc-email">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat k="Sent" v={num(e.sent)} sub={`${pct(e.delivery_rate)} delivered`} />
                <Stat k="Clicked" v={num(e.clicked)} sub={`${pct(e.click_rate)} click rate`} />
                <Stat k="Conversions" v={num(e.conversions)} sub={`${usd(e.revenue)} revenue`} />
                <Stat k="Unsubscribes" v={num(e.unsubscribes)} />
              </div>
            </Section>
            {d.funnel?.length > 0 && <Section title="Funnel"><Funnel rows={d.funnel} /></Section>}
          </>
        );
      }}</Body>
    </AdminShell>
  );
}
